import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { CheckboxType } from '@mlb/react-patterns/components/Checkbox/checkbox.types';

import { DateUtils } from 'bdvp-common/src/utils';

import { FILTER_TYPE } from '../../../utils/constants';
import FilterHeader from '../../FilterHeader';
import DateInputFilter from '../DateInput';
import DateRange from '../DateRange';
import MultiSelect from '../MultiSelect';

const DateInput = styled(DateInputFilter)`
    margin-bottom: 32px;
`;

const defaultOptions = [
    {
        label: 'Last 7 Days',
        value: 6,
    },
    {
        label: 'Last 15 Days',
        value: 14,
    },
    {
        label: 'Last 30 Days',
        value: 29,
    },
];

const configMultiSelect = { columnCount: 2 };

const DateCombined = (props) => {

    const {
        componentConfig,
        fields,
        onChange,
    } = props;

    const inputField = fields.find((field) => field.filterType === FILTER_TYPE.default);
    const rangeField = fields.find((field) => field.filterType === FILTER_TYPE.range);
    const dateValue = inputField.selected[0]?.value;
    const dateRangeValue = rangeField.selected[0]?.value;

    const options = componentConfig.selectOptions || defaultOptions;
    const defaultFieldsMultiSelect = useMemo(() => [
        {
            id: 'QUICK_SELECTION',
            options,
            selected: [],
        },
    ], [options]);

    const fieldsInput = useMemo(() => [inputField], [dateValue]);
    const {
        fieldsRange,
        fieldsQuickSelection,
    } = useMemo(() => {

        if (!dateRangeValue) {

            return {
                fieldsRange: [rangeField],
                fieldsQuickSelection: defaultFieldsMultiSelect,
            };

        }

        const [
            fromValue,
            toValue,
        ] = rangeField.selected[0].value.replace(/{|}|"| /g, '').split(',');
        const today = DateUtils.formatDate(DateUtils.date(), 'en', 'standard');

        if (toValue !== today) {

            return {
                fieldsRange: [rangeField],
                fieldsQuickSelection: defaultFieldsMultiSelect,
            };

        }

        const from = options.find(option => fromValue === DateUtils.formatDate(DateUtils.subDays(DateUtils.date(), option.value), 'en', 'standard'));

        if (from) {

            return {
                fieldsRange: [rangeField],
                fieldsQuickSelection: [
                    {
                        ...defaultFieldsMultiSelect[0],
                        selected: [from],
                    },
                ],
            };

        }

        return {
            fieldsRange: [rangeField],
            fieldsQuickSelection: defaultFieldsMultiSelect,
        };

    }, [dateRangeValue]);

    const onChangeQuickSelectionHandler = useCallback((data) => {

        if (data.event === 'select-option') {

            const fromDate = DateUtils.subDays(DateUtils.date(), data.changes.value);
            const toDate = DateUtils.date();
            const fromValue = DateUtils.formatDate(fromDate, 'en', 'standard');
            const toValue = DateUtils.formatDate(toDate, 'en', 'standard');
            const fromLabel = DateUtils.formatDate(fromDate, 'en', 'short');
            const toLabel = DateUtils.formatDate(toDate, 'en', 'short');

            const changes = {
                label: `${fromLabel} - ${toLabel}`,
                value: `{{ "${fromValue}", "${toValue}" }}`,
            };

            onChange({
                changes,
                event: data.event,
                selected: [changes],
            }, fieldsRange[0]);

        }
        if (data.event === 'deselect-option') {

            onChange({
                changes: fieldsRange[0].selected[0],
                event: data.event,
                selected: [],
            }, fieldsRange[0]);

        }

    }, [fieldsRange]);

    const disabledDate = !!!dateValue && !!dateRangeValue;
    const disabledDateRange = !!!dateRangeValue && !!dateValue;

    return (
        <>
            <FilterHeader text={'Date'} disabled={disabledDate} />
            <DateInput onChange={onChange} fields={fieldsInput} disabled={disabledDate} />
            <FilterHeader text={'Date Range'} disabled={disabledDateRange} />
            <MultiSelect
                onChange={onChangeQuickSelectionHandler}
                type={CheckboxType.CHECKBOX}
                componentConfig={configMultiSelect}
                filterName={'RANGE'}
                fields={fieldsQuickSelection}
                disabled={disabledDateRange}
            />
            <DateRange onChange={onChange} fields={fieldsRange} disabled={disabledDateRange} />
        </>
    );

};

DateCombined.propTypes = {
    onChange: PropTypes.func.isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            selected: PropTypes.array.isRequired,
        }).isRequired
    ).isRequired,
    componentConfig: PropTypes.object,
};

DateCombined.defaultProps = {
    componentConfig: {},
};

export default DateCombined;
